import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface TourStep {
  id: string;
  page: string;
  elementSelector?: string;
  title: string;
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  action?: () => void; // Optional action to perform when step is shown
}

interface GuidedTourContextType {
  isActive: boolean;
  currentStepIndex: number;
  tourSteps: TourStep[];
  startTour: () => void;
  exitTour: () => void;
  nextStep: () => void;
  previousStep: () => void;
  getCurrentStep: () => TourStep | null;
}

const GuidedTourContext = createContext<GuidedTourContextType | undefined>(undefined);

export const useGuidedTour = () => {
  const context = useContext(GuidedTourContext);
  if (!context) {
    throw new Error('useGuidedTour must be used within a GuidedTourProvider');
  }
  return context;
};

interface GuidedTourProviderProps {
  children: ReactNode;
}

export const GuidedTourProvider: React.FC<GuidedTourProviderProps> = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Define all tour steps
  const tourSteps: TourStep[] = [
    {
      id: 'welcome',
      page: '/settings',
      title: 'Welcome to the Guided Tour!',
      description: "Hi! I'm your guide. I'll walk you through all the features of this app. Let's start by exploring the navigation.",
      position: 'bottom',
    },
    {
      id: 'upload-nav',
      page: '/settings',
      elementSelector: '[href="/upload"]',
      title: 'Upload Button',
      description: 'This is where you can upload your diary entries or any text content. Click Next to visit the Upload page.',
      position: 'bottom',
    },
    {
      id: 'upload-page-intro',
      page: '/upload',
      title: 'Upload Page',
      description: 'Welcome to the Upload page! Here you can add your text content to the system. Let me show you the key features.',
      position: 'bottom',
    },
    {
      id: 'upload-source',
      page: '/upload',
      elementSelector: '#source',
      title: 'Category Selector',
      description: 'Select a category for your content. You can create custom categories to organize your entries.',
      position: 'bottom',
    },
    {
      id: 'upload-textarea',
      page: '/upload',
      elementSelector: '#content',
      title: 'Content Input',
      description: 'Type or paste your content here. ',
      position: 'top',
    },
    {
      id: 'upload-delimiter',
      page: '/upload',
      elementSelector: '#delimiter',
      title: 'Custom Delimiter',
      description: 'Optionally specify a delimiter to split your content into chunks. If left empty, the system will use ## as the delimiter. If no delimiter is found in your content, it will automatically split by every 10000 characters.',
      position: 'bottom',
    },
    {
      id: 'upload-button',
      page: '/upload',
      elementSelector: 'button[type="submit"]',
      title: 'Upload Button',
      description: "Once you've entered your content, click this button to process and store it. ",
      position: 'top',
    },
    {
      id: 'search-nav',
      page: '/upload',
      elementSelector: '[href="/search"]',
      title: 'Search Button',
      description: 'Now let\'s move to the Search page where you can find specific content in your uploaded entries.',
      position: 'bottom',
    },
    {
      id: 'search-page-intro',
      page: '/search',
      title: 'Search Page',
      description: 'This is where you search through all your uploaded content. Let me explain the features.',
      position: 'bottom',
    },
    {
      id: 'search-input',
      page: '/search',
      elementSelector: '.search-input',
      title: 'Search Input',
      description: 'Type your search query here. The system uses semantic search to find relevant chunks even if they don\'t contain exact keywords.',
      position: 'bottom',
    },
    {
      id: 'search-button',
      page: '/search',
      elementSelector: '.search-button',
      title: 'Search Button',
      description: 'Click this button to execute your search and see matching results.',
      position: 'bottom',
    },
    {
      id: 'chunk-cards',
      page: '/search',
      elementSelector: '.chunk-display',
      title: 'Search Results',
      description: 'Each card shows a chunk of content that matches your search. You can use keyboard shortcuts (→ to select, ← to discard, D to delete) or click the action buttons.',
      position: 'top',
    },
    {
      id: 'discard-button',
      page: '/search',
      elementSelector: '.discard-action',
      title: 'Discard Button',
      description: 'Click this button (or press ← arrow key) to discard a chunk for the current session. Discarded chunks won\'t appear in future searches within this session.',
      position: 'top',
    },
    {
      id: 'select-button',
      page: '/search',
      elementSelector: '.select-action',
      title: 'Select Button',
      description: 'Click this button (or press → arrow key) to select a chunk for the current session. Selected chunks can be reviewed and exported later.',
      position: 'top',
    },
    {
      id: 'delete-button',
      page: '/search',
      elementSelector: '.delete-button',
      title: 'Delete Permanently',
      description: 'This permanently deletes the current chunk from the database across all sessions. Use with caution - this action cannot be undone!',
      position: 'bottom',
    },
    {
      id: 'mark-done-button',
      page: '/search',
      elementSelector: '.done-button',
      title: 'Mark as Done',
      description: 'Mark this chunk as done across all sessions. Done chunks can be filtered out in your searches using the "Include Done" filter.',
      position: 'bottom',
    },
    {
      id: 'latest-button',
      page: '/search',
      elementSelector: '.latest-button',
      title: 'Latest Button',
      description: 'Click this to quickly retrieve the 150 most recently uploaded chunks without typing a search query.',
      position: 'bottom',
    },
    {
      id: 'session-dropdown',
      page: '/search',
      elementSelector: '.session-dropdown',
      title: 'Session Management',
      description: 'Create and switch between different sessions to organize your searches. Each session maintains its own search history and selected chunks.',
      position: 'bottom',
    },
    {
      id: 'selected-nav',
      page: '/search',
      elementSelector: '[href="/selected"]',
      title: 'Selected Button',
      description: 'All chunks you select will appear here. The number shows how many chunks are currently selected.',
      position: 'bottom',
    },
    {
      id: 'selected-page-intro',
      page: '/selected',
      title: 'Selected Chunks Page',
      description: 'This page shows all the chunks you\'ve marked as selected from your searches.',
      position: 'bottom',
    },
    {
      id: 'selected-chunks-list',
      page: '/selected',
      elementSelector: '.chunks-grid',
      title: 'Your Selected Chunks',
      description: 'Review and manage your selected chunks here. You can unselect or delete chunks as needed.',
      position: 'top',
    },
    {
      id: 'export-nav',
      page: '/selected',
      elementSelector: '[href="/export"]',
      title: 'Export Button',
      description: 'Ready to export? Let\'s head to the Export page.',
      position: 'bottom',
    },
    {
      id: 'export-page-intro',
      page: '/export',
      title: 'Export Page',
      description: 'Here you can export your selected chunks in various formats.',
      position: 'bottom',
    },
    {
      id: 'export-format',
      page: '/export',
      elementSelector: '#export-format',
      title: 'Export Format',
      description: 'Choose your preferred export format (JSON, text, or CSV).',
      position: 'top',
    },
    {
      id: 'export-button',
      page: '/export',
      elementSelector: '.export-button.primary',
      title: 'Export Button',
      description: 'Click this to download your selected chunks in the chosen format.',
      position: 'top',
    },
    {
      id: 'settings-nav',
      page: '/export',
      elementSelector: '[title="Settings"]',
      title: 'Settings Button',
      description: 'Let\'s return to Settings to wrap up the tour.',
      position: 'bottom',
    },
    {
      id: 'settings-page-final',
      page: '/settings',
      title: 'Tour Complete!',
      description: 'Congratulations! You\'ve completed the guided tour. You now know how to use all the main features of the app. Feel free to explore on your own or restart the tour anytime from this page.',
      position: 'bottom',
    },
  ];

  const startTour = () => {
    setIsActive(true);
    setCurrentStepIndex(0);
  };

  const exitTour = () => {
    setIsActive(false);
    setCurrentStepIndex(0);
  };

  const nextStep = () => {
    if (currentStepIndex < tourSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      exitTour();
    }
  };

  const previousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const getCurrentStep = () => {
    if (!isActive || currentStepIndex < 0 || currentStepIndex >= tourSteps.length) {
      return null;
    }
    return tourSteps[currentStepIndex];
  };

  return (
    <GuidedTourContext.Provider
      value={{
        isActive,
        currentStepIndex,
        tourSteps,
        startTour,
        exitTour,
        nextStep,
        previousStep,
        getCurrentStep,
      }}
    >
      {children}
    </GuidedTourContext.Provider>
  );
};
